import axios from "axios";
import { getCsrfTokenFromCookies, setCsrfTokenToCookies } from "../utils/csrf";
import store from "state/store";
import { LOGOUT } from "../state/actions/types";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_VERSION = process.env.REACT_APP_API_VERSION;

export const API_BASE_URL = `${BASE_URL}${API_VERSION}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request Interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const csrfToken = getCsrfTokenFromCookies();
  if (csrfToken) {
    config.headers["X-CSRF-Token"] = csrfToken;
  }

  return config;
});

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 403 &&
      error.response.data?.code === "csrf_token_mismatch"
    ) {
      try {
        // Refresh CSRF token
        const csrfResponse = await axios.get(`${API_BASE_URL}auth/refresh-csrf`, {
          withCredentials: true,
        });
        const newCsrfToken = csrfResponse.data?.data?.csrf_token;
        if (newCsrfToken) {
          setCsrfTokenToCookies(newCsrfToken);
          originalRequest.headers["X-CSRF-Token"] = newCsrfToken;
        }
        return api(originalRequest);
      } catch (csrfError) {
        return Promise.reject(csrfError);
      }
    }

    // Handle access token expiration (401 status)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = store.getState().auth?.user?.refreshToken;

      if (refreshToken) {
        try {
          // Refresh access token
          const refreshRes = await axios.post(
            `${API_BASE_URL}auth/refresh-token`,
            {
              refresh_token: refreshToken,
            },
            { withCredentials: true }
          );

          const newAccessToken = refreshRes.data.access_token;
          const newRefreshToken = refreshRes.data.refresh_token;

          localStorage.setItem("access_token", newAccessToken);
          localStorage.setItem("refresh_token", newRefreshToken);

          // Refresh CSRF token
          const csrfResponse = await axios.get(`${API_BASE_URL}auth/refresh-csrf`, {
            withCredentials: true,
          });
          const newCsrfToken = csrfResponse.data?.data?.csrf_token;
          if (newCsrfToken) {
            setCsrfTokenToCookies(newCsrfToken);
            originalRequest.headers["X-CSRF-Token"] = newCsrfToken;
          }

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          store.dispatch({ type: LOGOUT });
          window.location.href = "/#/auth/signin";
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
